import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Users, Target, Handshake } from '@phosphor-icons/react';

const values = [
  {
    icon: Leaf,
    title: 'Environmental Focus',
    description: 'Committed to sustainable practices and protecting our local environment for future generations.',
  },
  {
    icon: Users,
    title: 'Local Employment',
    description: 'Employing local people and investing in the North Somerset community.',
  },
  {
    icon: Target,
    title: 'Reliability',
    description: 'Consistent, dependable service delivery that businesses can count on.',
  },
  {
    icon: Handshake,
    title: 'Partnership',
    description: 'Working together with clients to achieve their waste management goals.',
  },
];

export const AboutSection = ({ isFullPage = false }) => {
  return (
    <section 
      className={`${isFullPage ? 'py-32' : 'py-24 lg:py-32'} bg-neutral-50`}
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="accent-font text-lime-600 mb-4">About NSEC</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-6">
              Your Local Business <span className="text-lime-600">Waste & Recycling</span> Solution
            </h2>
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              <p>
                At North Somerset Environment Company, we pride ourselves on being a local business, 
                serving the vibrant community of North Somerset. With a focus on providing competitive, 
                reliable, and cost-effective waste management solutions, we are here to support your 
                business in improving its recycling rate and reducing its environmental footprint.
              </p>
              <p>
                Our comprehensive services are designed to meet the diverse needs of businesses in the area. 
                Whether you operate a small shop, a bustling restaurant, or a large office complex, 
                we have tailored solutions to suit your requirements.
              </p>
              {isFullPage && (
                <>
                  <p>
                    We are committed to caring for our environment and fostering a culture of sustainability 
                    within the local business community. By partnering with us, you not only benefit from 
                    efficient waste management but also contribute to the preservation of our planet for 
                    future generations.
                  </p>
                  <p>
                    We take pride in supporting local businesses and employment opportunities in North Somerset. 
                    Our services are delivered with a personal touch, ensuring that you receive the attention 
                    and care you deserve.
                  </p>
                </>
              )}
            </div>
            {!isFullPage && (
              <Link
                to="/about"
                className="btn-primary inline-flex items-center gap-2 mt-8"
                data-testid="about-cta"
              >
                Learn More About Us
                <ArrowRight size={20} weight="bold" />
              </Link>
            )}
          </motion.div>

          {/* Values Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="p-6 rounded-2xl bg-white border border-neutral-200 hover:border-lime-300 hover:shadow-lg transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="inline-flex p-3 rounded-xl bg-lime-50 mb-4">
                  <value.icon size={28} weight="duotone" className="text-lime-600" />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-neutral-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-neutral-600 text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Coverage Map Section - Only on full page */}
        {isFullPage && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-24 pt-16 border-t border-neutral-200"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="font-outfit font-bold text-2xl lg:text-3xl text-neutral-900 mb-6">
                  Serving the <span className="text-lime-600">South West</span>
                </h3>
                <p className="text-neutral-600 leading-relaxed mb-6">
                  Based in Weston-super-Mare, we provide our comprehensive range of services 
                  across North Somerset and the wider South West region. Our local presence 
                  means we understand the unique needs of businesses in our community.
                </p>
                <div className="space-y-3 text-neutral-600">
                  <p><strong className="text-neutral-900">Coverage includes:</strong></p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>North Somerset</li>
                    <li>Bristol</li>
                    <li>Bath & North East Somerset</li>
                    <li>South Gloucestershire</li>
                    <li>Surrounding areas</li>
                  </ul>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-lg">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/65f818232111306524528f63/2b5dfae9-338c-4047-9d29-71a43eb28790/coverage+map.JPG"
                  alt="NSEC Coverage Map"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
